import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Flex, HStack, Select, Tab, TabList, TabPanel, TabPanels, Tabs, Text,
} from "@chakra-ui/react";
import { getMlAdsOpcoes, getMlAdsRegras } from "../services/api";
import CampanhasTab from "../components/PublicidadeML/CampanhasTab";
import AnunciosTab from "../components/PublicidadeML/AnunciosTab";
import RegrasTab from "../components/PublicidadeML/RegrasTab";
import HistoricoTab from "../components/PublicidadeML/HistoricoTab";
import { PERIODOS, VERDE, isoDiasAtras } from "../components/PublicidadeML/comum";

const CHAVE_ABA = "publicidade_ml_aba";

const lerAba = () => {
  try {
    return Number(window.localStorage.getItem(CHAVE_ABA)) || 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Publicidade ML (Product Ads): campanhas das contas do Mercado Livre, os
 * anúncios dentro delas e as regras que colocam e tiram anúncios sozinhas.
 */
export default function PublicidadeML() {
  const [opcoes, setOpcoes] = useState(null);
  const [regras, setRegras] = useState(null);
  const [carregandoRegras, setCarregandoRegras] = useState(false);
  const [conta, setConta] = useState("");
  const [dias, setDias] = useState(30);
  const [aba, setAba] = useState(lerAba);

  const periodo = useMemo(() => ({ de: isoDiasAtras(dias), ate: isoDiasAtras(1) }), [dias]);

  const carregarOpcoes = useCallback(async () => {
    setOpcoes(await getMlAdsOpcoes());
  }, []);

  const carregarRegras = useCallback(async () => {
    setCarregandoRegras(true);
    setRegras(await getMlAdsRegras());
    setCarregandoRegras(false);
  }, []);

  useEffect(() => {
    carregarOpcoes();
    carregarRegras();
  }, [carregarOpcoes, carregarRegras]);

  const mudarAba = (i) => {
    setAba(i);
    try {
      window.localStorage.setItem(CHAVE_ABA, String(i));
    } catch (e) {
      // sem armazenamento local: só não lembra a aba
    }
  };

  const recarregarTudo = () => {
    carregarOpcoes();
    carregarRegras();
  };

  const contas = opcoes?.contas || [];

  return (
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align={{ base: "stretch", md: "center" }} mb={3} gap={3} direction={{ base: "column", md: "row" }}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={VERDE}>Publicidade ML</Text>
          <Text fontSize="sm" color="gray.500">
            Product Ads das contas do Mercado Livre: campanhas, anúncios e regras que colocam e tiram anúncios sozinhas.
          </Text>
        </Box>
        <Flex gap={2} flexWrap="wrap" align="center">
          <Select size="sm" w={{ base: "100%", sm: "240px" }} bg="white" value={conta} onChange={(e) => setConta(e.target.value)}>
            <option value="">Todas as contas</option>
            {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </Select>
          <HStack spacing={1} overflowX="auto" maxW="100%">
            {PERIODOS.map((p) => (
              <Button key={p.dias} size="xs" flexShrink={0} variant={dias === p.dias ? "solid" : "outline"} bg={dias === p.dias ? undefined : "white"}
                colorScheme={dias === p.dias ? "orange" : "gray"} onClick={() => setDias(p.dias)}>
                {p.label}
              </Button>
            ))}
          </HStack>
        </Flex>
      </Flex>

      {opcoes === null && (
        <Alert status="info" mb={3} borderRadius="md" fontSize="sm"><AlertIcon />Carregando as contas do Mercado Livre...</Alert>
      )}
      {contas.filter((c) => c.erro).map((c) => (
        <Alert key={c.id} status="warning" mb={3} borderRadius="md" fontSize="sm"><AlertIcon />{c.nome}: {c.erro}</Alert>
      ))}

      <Box bg="white" borderRadius="lg" boxShadow="sm">
        <Tabs colorScheme="orange" index={aba} onChange={mudarAba} isLazy>
          <TabList px={{ base: 1, md: 4 }} overflowX="auto" overflowY="hidden" whiteSpace="nowrap">
            <Tab fontWeight="bold" fontSize="sm" flexShrink={0}>Campanhas</Tab>
            <Tab fontWeight="bold" fontSize="sm" flexShrink={0}>Anúncios</Tab>
            <Tab fontWeight="bold" fontSize="sm" flexShrink={0}>Regras</Tab>
            <Tab fontWeight="bold" fontSize="sm" flexShrink={0}>Histórico</Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              <CampanhasTab conta={conta} periodo={periodo} opcoes={opcoes} onMudou={recarregarTudo} />
            </TabPanel>
            <TabPanel p={0}>
              <AnunciosTab conta={conta} periodo={periodo} opcoes={opcoes} regras={regras} />
            </TabPanel>
            <TabPanel p={0}>
              <RegrasTab opcoes={opcoes} regras={regras} carregando={carregandoRegras} onRecarregar={recarregarTudo} />
            </TabPanel>
            <TabPanel p={0}>
              <HistoricoTab conta={conta} regras={regras} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
